<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  claimTaxYearConfig,
  useClaimTaxBack,
} from '@/composables/useClaimTaxBack'
import { formatMoneyFromPence, formatHmrcAmountFromPence } from '@/helpers/moneyDisplay'
import { donationsRouteForPeriod } from '@/helpers/givingRecord'
import { findIncomeBandById } from '@/helpers/claimTaxBack/taxYearConfig'
import {
  ratePhraseIdForBand,
  recordsKeepUntilYear,
  selfAssessmentDeadlineYears,
} from '@/helpers/claimTaxBack/guideContent'
import type { ClaimRoute } from '@/types/claimTaxBack'
import type { DonorDonation } from '@/types/donorFinancialYear'
import ClaimGuideStepIndex from '@/components/dashboard/claim/ClaimGuideStepIndex.vue'
import DashboardFormField from '@/components/dashboard/ui/DashboardFormField.vue'
import ClaimGuideCollapse from '@/components/dashboard/claim/ClaimGuideCollapse.vue'
import DashboardHint from '@/components/dashboard/ui/DashboardHint.vue'
import { usePersonalDetailsDisplay } from '@/composables/usePersonalDetailsDisplay'
import {
  buildClaimLetterContent,
  formatClaimLetterDate,
} from '@/helpers/claimTaxBack/claimLetter'
import {
  maskNationalInsuranceInput,
  NATIONAL_INSURANCE_DISPLAY_LENGTH,
  nationalInsuranceCaretIndex,
  parseNationalInsuranceNumber,
} from '@/helpers/claimTaxBack/nationalInsurance'
import { downloadClaimLetterPdf } from '@/services/claimLetter/buildClaimLetterPdf'
import { downloadClaimLetterDocx } from '@/services/claimLetter/buildClaimLetterDocx'

/**
 * Phone keeps numbered steps, a donations list in case HMRC asks, and
 * copyable wording for the call. Self Assessment keeps the same steps and
 * list, plus the Gift Aided donations total in the format HMRC's box accepts.
 * The letter route is a single job: what we filled in, National Insurance
 * number in this browser only, then download, sign and post. The number is
 * never saved to the account. The claim snapshot lives in step 2.
 * Instructions wait until donations are confirmed so we never send an
 * incomplete total. When the account total is £0 the steps stay generic:
 * we never tell them to enter or quote £0.00.
 */
const props = defineProps<{
  route: ClaimRoute
  totalGivenPence: number
  donationsConfirmed: boolean
  donations: DonorDonation[]
}>()

const { t } = useI18n()
const { profile } = useClaimTaxBack()
const { fullName, addressLines } = usePersonalDetailsDisplay()

const yearLabel = claimTaxYearConfig.yearLabel
const periodKey = claimTaxYearConfig.periodKey
const hmrcPhoneNumber = claimTaxYearConfig.hmrcPhoneNumber
const hmrcPhoneHours = claimTaxYearConfig.hmrcPhoneHours
const hmrcPostalAddress = claimTaxYearConfig.hmrcPostalAddress
const saDeadlines = selfAssessmentDeadlineYears(periodKey)
// HMRC's 22 month record keeping window, as a concrete date for the claim year.
const keepUntilYear = recordsKeepUntilYear(periodKey)

const givenDisplay = computed(() => formatMoneyFromPence(props.totalGivenPence))
const hmrcAmount = computed(() =>
  formatHmrcAmountFromPence(props.totalGivenPence)
)
const hasNoGiving = computed(() => props.totalGivenPence === 0)

const ratePhrase = computed(() => {
  const bandId = profile.value?.incomeBandId
  if (!bandId) return t('Dashboard.ClaimTaxBack.Guide.Route.Rate.higher')
  const band = findIncomeBandById(claimTaxYearConfig, bandId)
  if (!band) return t('Dashboard.ClaimTaxBack.Guide.Route.Rate.higher')
  return t(
    `Dashboard.ClaimTaxBack.Guide.Route.Rate.${ratePhraseIdForBand(band)}`
  )
})

const vars = computed(() => ({
  amount: givenDisplay.value,
  year: yearLabel,
  phone: hmrcPhoneNumber,
  hours: hmrcPhoneHours,
  rate: ratePhrase.value,
  fileByYear: saDeadlines.fileByYear,
  amendByYear: saDeadlines.amendByYear,
  keepUntilYear,
}))

/** Hint copy can run to more than one paragraph; split on blank lines. */
const hintParagraphs = (key: string): string[] =>
  t(`Dashboard.ClaimTaxBack.Guide.Route.${key}`, vars.value)
    .split(/\n\n/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0)

/**
 * Record keeping lines that close step 3. Each has a short sentence on
 * screen and the detail behind a ? hint so the step does not grow further.
 */
const recordsLines = computed(() => [
  {
    key: 'keep',
    text: t('Dashboard.ClaimTaxBack.Guide.Route.RecordsKeep', vars.value),
    trigger: t(
      'Dashboard.ClaimTaxBack.Guide.Route.RecordsKeepHintTrigger',
      vars.value
    ),
    hint: hintParagraphs('RecordsKeepHint'),
  },
  {
    key: 'proof',
    text: t('Dashboard.ClaimTaxBack.Guide.Route.RecordsProof'),
    trigger: t('Dashboard.ClaimTaxBack.Guide.Route.RecordsProofHintTrigger'),
    hint: hintParagraphs('RecordsProofHint'),
  },
])

/** True when we have a real route and a confirmed giving total to quote. */
const showRoute = computed(
  () => props.donationsConfirmed && props.route.id !== 'unknown'
)

const isWrite = computed(
  () =>
    props.route.id === 'paye-writing' ||
    props.route.id === 'paye-writing-itemised'
)

const routeKey = computed(() => {
  switch (props.route.id) {
    case 'self-assessment':
      return 'SelfAssessment'
    case 'paye-phone':
      return 'PayePhone'
    case 'paye-writing-itemised':
      return 'PayeWritingItemised'
    case 'paye-writing':
      return 'PayeWriting'
    default:
      return 'Unknown'
  }
})

const routeTitle = computed(() => {
  if (!showRoute.value) {
    return t('Dashboard.ClaimTaxBack.Guide.Route.Unknown.Title')
  }
  if (props.route.id === 'paye-writing-itemised') {
    return t('Dashboard.ClaimTaxBack.Guide.Route.PayeWritingItemised.Title')
  }
  return t(`Dashboard.ClaimTaxBack.Guide.Route.${routeKey.value}.Title`)
})

const routeIntro = computed(() => {
  if (hasNoGiving.value) {
    if (props.route.id === 'paye-writing-itemised') {
      return t('Dashboard.ClaimTaxBack.Guide.Route.PayeWritingItemised.PreviewIntro')
    }
    if (props.route.id === 'paye-writing') {
      return t('Dashboard.ClaimTaxBack.Guide.Route.PayeWriting.PreviewIntro')
    }
    if (props.route.id === 'paye-phone') {
      return t(
        'Dashboard.ClaimTaxBack.Guide.Route.PayePhone.PreviewIntro',
        vars.value
      )
    }
    if (props.route.id === 'self-assessment') {
      return t(
        'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.PreviewIntro',
        vars.value
      )
    }
  }
  if (props.route.id === 'paye-writing-itemised') {
    return t(
      'Dashboard.ClaimTaxBack.Guide.Route.PayeWritingItemised.Intro',
      vars.value
    )
  }
  return t(
    `Dashboard.ClaimTaxBack.Guide.Route.${routeKey.value}.Intro`,
    vars.value
  )
})

type GuideStep = { title: string; body: string }

const steps = computed<GuideStep[]>(() => {
  const v = vars.value
  if (props.route.id === 'self-assessment') {
    return [
      {
        title: t(
          'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.Step1Title'
        ),
        body: t('Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.Step1Body'),
      },
      {
        title: t(
          hasNoGiving.value
            ? 'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.PreviewStep2Title'
            : 'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.Step2Title',
          v
        ),
            body: t(
              'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.Step2Body',
              v
            ),
      },
      {
        title: t(
          'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.Step3Title',
          v
        ),
        body: t(
          'Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.Step3Body',
          v
        ),
      },
    ]
  }
  if (props.route.id === 'paye-phone') {
    return [
      {
        title: t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Step1Title'),
        body: t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Step1Body', v),
      },
      {
        title: t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Step2Title'),
        body: t(
          hasNoGiving.value
            ? 'Dashboard.ClaimTaxBack.Guide.Route.PayePhone.PreviewStep2Body'
            : 'Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Step2Body',
          v
        ),
      },
      {
        title: t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Step3Title'),
        body: t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Step3Body'),
      },
    ]
  }
  return []
})

const scriptLabel = computed(() =>
  t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.ScriptLabel')
)

const scriptBody = computed(() =>
  t('Dashboard.ClaimTaxBack.Guide.Route.PayePhone.Script', vars.value)
)

const letterInput = computed(() => ({
  senderName: fullName.value.trim() === ',' ? '' : fullName.value.trim(),
  senderAddressLines: addressLines.value,
  letterDate: formatClaimLetterDate(),
  yearLabel,
  givenPence: props.totalGivenPence,
  ratePhrase: ratePhrase.value,
  includeItemisedList: props.route.requiresItemisedList,
  donations: props.donations,
}))

const letterFromLines = computed(() => {
  const letter = buildClaimLetterContent(letterInput.value)
  if (letter.senderName)
    return [letter.senderName, ...letter.senderAddressLines]
  return letter.senderAddressLines
})

const hasLetterIdentity = computed(() => letterFromLines.value.length > 0)

const nationalInsuranceNumber = ref('')
const niError = ref<string | null>(null)

const letterError = ref<string | null>(null)
const isDownloadingLetter = ref(false)

const parsedNi = computed(() =>
  parseNationalInsuranceNumber(nationalInsuranceNumber.value)
)

// Uppercase and space like QQ 12 34 56 C as they type. Cap at 9 letters/digits.
// Full format is still only checked on download, not mid-entry.
const onNiInput = (event: Event) => {
  niError.value = null
  const input = event.target as HTMLInputElement
  const caret = nationalInsuranceCaretIndex(
    input.value,
    input.selectionStart ?? input.value.length
  )
  const masked = maskNationalInsuranceInput(input.value)
  nationalInsuranceNumber.value = masked
  // Restore the caret after Vue writes the spaced value back to the input.
  void nextTick(() => {
    input.setSelectionRange(caret, caret)
  })
}

const validateLetterFields = (): boolean => {
  niError.value = parsedNi.value
    ? null
    : t('Dashboard.ClaimTaxBack.Guide.Route.LetterAddNiError')
  return !niError.value
}

const downloadLetter = async (format: 'pdf' | 'docx') => {
  letterError.value = null
  if (!validateLetterFields() || !parsedNi.value) return
  isDownloadingLetter.value = true
  try {
    // Date is taken at click time so an overnight tab still posts today's letter.
    // National Insurance stays in memory for this download only.
    const letter = buildClaimLetterContent({
      ...letterInput.value,
      letterDate: formatClaimLetterDate(),
      nationalInsuranceNumber: parsedNi.value,
    })
    if (format === 'pdf') {
      await downloadClaimLetterPdf(letter)
    } else {
      downloadClaimLetterDocx(letter)
    }
  } catch (error) {
    console.error('Failed to download HMRC claim letter', error)
    letterError.value = t(
      'Dashboard.ClaimTaxBack.Guide.Route.LetterDownloadFailed'
    )
  } finally {
    isDownloadingLetter.value = false
  }
}

const copied = ref(false)
const copyFailed = ref(false)
let copiedTimer: ReturnType<typeof setTimeout> | null = null

const copyToClipboard = async (text: string) => {
  copyFailed.value = false
  try {
    await navigator.clipboard.writeText(text)
    copied.value = true
    if (copiedTimer) clearTimeout(copiedTimer)
    copiedTimer = setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch {
    copyFailed.value = true
  }
}

const copyWording = () => copyToClipboard(scriptBody.value)
const copyAmount = () => copyToClipboard(hmrcAmount.value)
</script>

<template>
  <section
    class="sa-claim-guide__step"
    aria-labelledby="claim-guide-route-heading"
  >
    <div class="sa-claim-guide__step-header">
      <ClaimGuideStepIndex :number="3" :pending="!showRoute" />
      <div class="sa-claim-guide__step-copy">
        <h3 id="claim-guide-route-heading" class="sa-claim-guide__step-title">
          <span class="sa-sr-only">
            {{ t('Dashboard.ClaimTaxBack.Guide.StepLabel', { number: 3 }) }}.
          </span>
          {{ routeTitle }}
        </h3>
      </div>
    </div>

    <div v-if="route.id === 'unknown'" class="sa-claim-guide__step-main">
      <p class="sa-claim-guide__step-body">
        {{ t('Dashboard.ClaimTaxBack.Guide.Route.Unknown.Body') }}
      </p>
    </div>
    <div v-else-if="!donationsConfirmed" class="sa-claim-guide__step-main">
      <p class="sa-claim-guide__step-body">
        {{ t('Dashboard.ClaimTaxBack.Guide.Route.WaitingForDonations.Body') }}
      </p>
    </div>

    <ClaimGuideCollapse :show="showRoute">
      <p class="sa-sr-only" aria-live="polite">
        {{ copied ? t('Dashboard.ClaimTaxBack.Guide.Route.Copied') : '' }}
      </p>
      <p
        v-if="hasNoGiving"
        class="sa-claim-guide__compare sa-claim-guide__compare--shortfall sa-claim-guide__compare--route-notice"
      >
        {{ t('Dashboard.ClaimTaxBack.Guide.Route.ZeroGivingNotice') }}
      </p>
      <p class="sa-claim-guide__step-body">{{ routeIntro }}</p>

      <template v-if="!isWrite">
        <ol class="sa-claim-guide__guide-steps">
          <li v-for="(step, index) in steps" :key="index">
            <span class="sa-claim-guide__guide-step-title">{{ step.title }}</span>
            <span class="sa-claim-guide__guide-step-body">{{ step.body }}</span>
          </li>
        </ol>

        <template v-if="route.id === 'self-assessment' && !hasNoGiving">
          <h4 class="sa-claim-guide__phone-heading">
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.SelfAssessment.AmountHeading') }}
          </h4>
          <p class="sa-claim-guide__phone">{{ hmrcAmount }}</p>
          <div class="sa-claim-guide__script-actions">
            <button
              type="button"
              class="sa-btn-secondary"
              :aria-label="
                t('Dashboard.ClaimTaxBack.Guide.Route.CopyAmountAria', {
                  amount: hmrcAmount,
                })
              "
              @click="copyAmount"
            >
              {{
                copied
                  ? t('Dashboard.ClaimTaxBack.Guide.Route.Copied')
                  : t('Dashboard.ClaimTaxBack.Guide.Route.CopyAmount')
              }}
            </button>
          </div>
          <p v-if="copyFailed" class="sa-field-error" role="alert">
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.CopyFailed') }}
          </p>
        </template>

        <template v-if="route.id === 'paye-phone'">
          <h4 class="sa-claim-guide__phone-heading">
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.PhoneHeading') }}
          </h4>
          <p class="sa-claim-guide__phone">
            <a :href="`tel:${hmrcPhoneNumber.replace(/\s/g, '')}`">
              {{ hmrcPhoneNumber }}
            </a>
          </p>
        </template>
      </template>

      <div v-if="isWrite && !hasNoGiving" class="sa-claim-guide__letter">
        <h4 class="sa-claim-guide__letter-heading">
          {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterHeading') }}
        </h4>

        <dl class="sa-claim-guide__letter-filled">
          <div class="sa-claim-guide__letter-filled-row">
            <dt>
              {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterFilledName') }}
            </dt>
            <dd>
              <template v-if="hasLetterIdentity">
                <span
                  v-for="(line, index) in letterFromLines"
                  :key="index"
                  class="sa-claim-guide__letter-line"
                >
                  {{ line }}
                </span>
              </template>
              <span v-else>
                {{
                  t('Dashboard.ClaimTaxBack.Guide.Route.LetterFilledNameMissing')
                }}
              </span>
              <router-link class="sa-claim-link-btn" :to="{ name: 'Account' }">
                {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterCheckDetails') }}
              </router-link>
            </dd>
          </div>
          <div class="sa-claim-guide__letter-filled-row">
            <dt>
              {{
                t('Dashboard.ClaimTaxBack.Guide.Route.LetterFilledGiving', {
                  year: yearLabel,
                })
              }}
            </dt>
            <dd>{{ givenDisplay }}</dd>
          </div>
          <div class="sa-claim-guide__letter-filled-row">
            <dt>
              {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterFilledList') }}
              <p class="sa-claim-guide__letter-filled-help">
                {{
                  t(
                    props.route.requiresItemisedList
                      ? 'Dashboard.ClaimTaxBack.Guide.Route.LetterFilledListHelpRequired'
                      : 'Dashboard.ClaimTaxBack.Guide.Route.LetterFilledListHelp'
                  )
                }}
              </p>
            </dt>
            <dd>
              <router-link
                class="sa-claim-link-btn sa-claim-link-btn--inline"
                :to="donationsRouteForPeriod(periodKey)"
              >
                {{ t('Dashboard.ClaimTaxBack.Guide.Route.ItemisedLink') }}
              </router-link>
            </dd>
          </div>
        </dl>

        <div class="sa-claim-guide__letter-add">
          <DashboardFormField
            :label="t('Dashboard.ClaimTaxBack.Guide.Route.LetterAddNi')"
            label-for="claim-letter-nino"
            :error="niError ?? undefined"
          >
            <template #default="{ errorId, hasError }">
              <p
                id="claim-letter-nino-help"
                class="sa-claim-guide__letter-todo-help"
              >
                {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterAddNiHelp') }}
              </p>
              <input
                id="claim-letter-nino"
                class="sa-input sa-input--ni"
                type="text"
                name="claim-letter-nino"
                autocomplete="off"
                autocapitalize="characters"
                spellcheck="false"
                inputmode="text"
                :maxlength="NATIONAL_INSURANCE_DISPLAY_LENGTH"
                :value="nationalInsuranceNumber"
                :placeholder="
                  t('Dashboard.ClaimTaxBack.Guide.Route.LetterAddNiPlaceholder')
                "
                :aria-invalid="hasError || undefined"
                :aria-describedby="
                  hasError
                    ? `claim-letter-nino-help ${errorId}`
                    : 'claim-letter-nino-help'
                "
                @input="onNiInput"
              />
            </template>
          </DashboardFormField>
        </div>

        <div class="sa-claim-guide__letter-sign-note">
          <p class="sa-claim-guide__letter-sign-title">
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterDownloadTitle') }}
          </p>
          <p class="sa-claim-guide__letter-todo-help">
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterDownloadHelp') }}
          </p>
        </div>
        <div class="sa-claim-guide__script-actions">
          <button
            type="button"
            class="sa-btn-primary sa-btn-primary--compact"
            :disabled="isDownloadingLetter"
            :aria-busy="isDownloadingLetter"
            @click="downloadLetter('pdf')"
          >
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterDownloadPdf') }}
          </button>
          <button
            type="button"
            class="sa-btn-secondary"
            :disabled="isDownloadingLetter"
            :aria-busy="isDownloadingLetter"
            @click="downloadLetter('docx')"
          >
            {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterDownloadWord') }}
          </button>
        </div>
        <p v-if="letterError" class="sa-field-error" role="alert">
          {{ letterError }}
        </p>

        <h4 class="sa-claim-guide__letter-subheading">
          {{ t('Dashboard.ClaimTaxBack.Guide.Route.LetterPostHeading') }}
        </h4>
        <address class="sa-claim-guide__address sa-claim-guide__address--in-letter">
          <span v-for="line in hmrcPostalAddress" :key="line">{{ line }}</span>
        </address>
        <i18n-t
          keypath="Dashboard.ClaimTaxBack.Guide.Route.LetterAfterPost"
          tag="p"
          class="sa-claim-guide__letter-after"
        >
          <template #phone>
            <a :href="`tel:${hmrcPhoneNumber.replace(/\s/g, '')}`">
              {{ hmrcPhoneNumber }}
            </a>
          </template>
        </i18n-t>
      </div>

      <div v-else-if="route.id === 'paye-phone' && !hasNoGiving" class="sa-claim-guide__script">
        <p class="sa-claim-guide__script-label">{{ scriptLabel }}</p>
        <p class="sa-claim-guide__script-body">{{ scriptBody }}</p>
        <div class="sa-claim-guide__script-actions">
          <button
            type="button"
            class="sa-btn-secondary"
            @click="copyWording"
          >
            {{
              copied
                ? t('Dashboard.ClaimTaxBack.Guide.Route.Copied')
                : t('Dashboard.ClaimTaxBack.Guide.Route.CopyWording')
            }}
          </button>
        </div>
        <p v-if="copyFailed" class="sa-field-error" role="alert">
          {{ t('Dashboard.ClaimTaxBack.Guide.Route.CopyFailed') }}
        </p>
      </div>

      <!-- Closes every route: HMRC's record keeping rule at the point they
           are about to claim, with the way back to the records themselves. -->
      <aside
        class="sa-claim-guide__records"
        aria-labelledby="claim-guide-records-heading"
      >
        <h4
          id="claim-guide-records-heading"
          class="sa-claim-guide__records-title"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Route.RecordsTitle') }}
        </h4>
        <ul class="sa-claim-guide__records-list">
          <li
            v-for="line in recordsLines"
            :key="line.key"
            class="sa-claim-guide__records-item"
          >
            <span>{{ line.text }}</span>
            <DashboardHint placement="top" :trigger-label="line.trigger">
              <p v-for="(paragraph, index) in line.hint" :key="index">
                {{ paragraph }}
              </p>
            </DashboardHint>
          </li>
        </ul>
        <router-link
          class="sa-claim-link-btn"
          :to="donationsRouteForPeriod(periodKey)"
        >
          {{ t('Dashboard.ClaimTaxBack.Guide.Route.ItemisedLink') }}
        </router-link>
      </aside>
    </ClaimGuideCollapse>
  </section>
</template>
